import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Rupt from 'rupt';
import { useMutation, useQuery } from 'urql';

import { fullLogout, isUserTrialExpired, useAuthStore } from '@revelio/auth';
import { Loading } from '@revelio/core';
import {
  type GetUserDetailsQuery,
  MetadataKey,
  USER_DETAILS_QUERY,
} from '@revelio/data-access';

import { environment } from '../../environments/environment';
import { FirstLoadOverlay } from '../../shared/components/first-load-overlay';
import { UpdateUserMutation } from '../adminRewrite/userOperations';
import styles from './auth-gate.module.css';
import { useDashMetaCsrf } from './useDashMetaCsrf';
import { useImpersonationTracking } from './useImpersonationTracking';
import { useUserTracking } from './useUserTracking';
import { useWhoAmI } from './useWhoAmI';

// Trial accounts created on/before 2026-07-27 (UTC) predate the portal's new
// signup + personalization flow, so they must never be bounced to
// /personalize. Only accounts created from 2026-07-28 (UTC) onward — which
// actually passed through (or abandoned) that step — are eligible.
const PERSONALIZATION_REDIRECT_CUTOFF = Date.parse('2026-07-28T00:00:00Z');

// How long to let the write become readable before re-reading it, and the cap
// on waiting for that re-read to come back. Only ever costs this on the
// un-personalized trial path, so a user who genuinely skipped the step is the
// only one who waits.
const PERSONALIZATION_RECHECK_MS = 1200;
const PERSONALIZATION_RECHECK_SETTLE_MS = 1500;

// The portal appends this when it sends a user back after they SUBMITTED the
// form — not merely after we sent them there. It's the backstop for a write
// that takes longer to become readable than the re-read above waits: without
// a "they did it" signal the user bounces straight back and has to submit
// again, re-sending the CRM lead every lap. Keying off our own redirect
// instead would let anyone skip the step by ignoring it once.
const PERSONALIZED_PARAM = 'personalized';

// Per-tab: the stale-read window is measured in seconds, but a refresh or a
// deeplink within the same visit must not re-trap the user. A fresh tab starts
// clean, so a user who never finishes is prompted again next visit.
// sessionStorage throws rather than no-ops where storage is blocked (Safari
// private mode, embedded webviews) — a storage failure must never break the
// gate, so both accessors fall back to today's behaviour.
const PERSONALIZED_THIS_SESSION_KEY = 'personalization-submitted';

const readPersonalizedThisSession = () => {
  try {
    return sessionStorage.getItem(PERSONALIZED_THIS_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const writePersonalizedThisSession = (submitted: boolean) => {
  try {
    if (submitted) {
      sessionStorage.setItem(PERSONALIZED_THIS_SESSION_KEY, '1');
    } else {
      sessionStorage.removeItem(PERSONALIZED_THIS_SESSION_KEY);
    }
  } catch {
    // Storage blocked — the in-memory value still covers this page load.
  }
};

// Consume the marker on arrival: record it for the tab and strip it from the
// URL so it doesn't survive into shared or bookmarked links.
const consumePersonalizedParam = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get(PERSONALIZED_PARAM) !== '1') {
    return false;
  }
  url.searchParams.delete(PERSONALIZED_PARAM);
  window.history.replaceState({}, '', url.toString());
  writePersonalizedThisSession(true);
  return true;
};

const AuthGateContent = ({ children }: PropsWithChildren) => {
  useUserTracking();
  useImpersonationTracking();

  return (
    <>
      {children}
      {/* Takes over from the auth loader above in the same viewport position,
          and holds until the first page has painted. */}
      <FirstLoadOverlay />
    </>
  );
};

export const AuthGate = ({ children }: PropsWithChildren) => {
  const { ory, dashMetaCsrfToken, isAuthenticated, user } = useAuthStore();
  const setUser = useAuthStore((state) => state.setUser);
  const ruptInitialized = useRef(false);

  const { isLoading: oryIsLoading } = useWhoAmI(ory);
  const { isLoading: dashMetaCsrfIsLoading } = useDashMetaCsrf();

  const isAuthenticating = oryIsLoading || dashMetaCsrfIsLoading;
  const userId = ory?.id;
  const email = user?.email ?? ory?.email;
  const accountCreatedAt = ory?.account_created_at;
  const isProd = environment.production;

  const [{ fetching, data }, refetchUser] = useQuery<GetUserDetailsQuery>({
    query: USER_DETAILS_QUERY,
    variables: { ids: userId ? [userId] : [] },
    pause: !userId,
  });

  const authorization = useMemo(() => {
    if (!data) {
      return {
        isAuthorized: false,
        user: undefined,
        needsPersonalization: false,
        error: undefined,
      };
    }

    try {
      const resolvedUser = data.users?.[0];
      if (!resolvedUser) throw new Error('USER_NOT_FOUND');
      if (!resolvedUser.active) throw new Error('USER_NOT_ACTIVE');
      if (isUserTrialExpired(resolvedUser)) {
        throw new Error('USER_TRIAL_EXPIRED');
      }

      // A trial user who bailed out of signup before the "tell us about
      // yourself" step has no personalization_completed stamp — the portal
      // collects it on /personalize, so the dashboard sends them there
      // rather than letting a direct visit skip the step. Expired trials
      // threw above, so they keep winning.
      //
      // Gate on the account-creation date so this only ever catches accounts
      // born into the new signup flow. Pre-existing trial users also lack the
      // stamp, so without this they'd all be redirected the moment the portal
      // flag flips on. Read the Ory identity's ISO date — NOT the metadata
      // `created_at`, whose Go time.String() value (e.g. "…+0000 UTC m=+…")
      // Date.parse can't read; a missing/unparseable date fails safe to "no
      // redirect" so an existing user is never trapped.
      const accountCreatedAtMs = Date.parse(accountCreatedAt ?? '');
      const createdInNewSignupEra =
        Number.isFinite(accountCreatedAtMs) &&
        accountCreatedAtMs >= PERSONALIZATION_REDIRECT_CUTOFF;

      const needsPersonalization =
        !resolvedUser.live &&
        createdInNewSignupEra &&
        !resolvedUser.metadata?.some(
          (entry) =>
            String(entry?.key) === 'personalization_completed' && !!entry?.value
        );

      return {
        isAuthorized: true,
        user: resolvedUser,
        needsPersonalization,
        error: undefined,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `${err}`;
      return {
        isAuthorized: false,
        user: undefined,
        needsPersonalization: false,
        error: errorMessage,
      };
    }
  }, [data, accountCreatedAt]);

  // Read once, on mount, before any redirect decision: the marker only exists
  // on the load the portal sends the user back on.
  const submittedThisSession = useRef(
    consumePersonalizedParam() || readPersonalizedThisSession()
  );
  // Separate from the ref: firing the redirect must not un-hold the loader,
  // or the dashboard paints behind a navigation that hasn't landed yet.
  const [redirectFired, setRedirectFired] = useState(false);

  // A missing stamp is not proof the user skipped the step — the portal writes
  // it and sends them straight here, and that write is not always readable yet
  // (the account in the reported incident HAS the stamp; only the read was
  // behind). So confirm with one fresh read before acting on it, rather than
  // bouncing a user who has already done the work. `network-only` because a
  // cached document would just repeat the same stale answer.
  // The guard is a ref, not state: setting state here would re-run this effect
  // and its cleanup would cancel the timer before it ever fired. If the
  // re-read comes back with the stamp, `needsPersonalization` goes false and
  // the redirect below simply never arms — the flag only has to release the
  // hold for the case where the stamp really is absent.
  const recheckScheduled = useRef(false);
  const [recheckDone, setRecheckDone] = useState(false);

  useEffect(() => {
    if (!authorization.isAuthorized || !authorization.needsPersonalization) {
      return;
    }
    if (recheckScheduled.current) {
      return;
    }
    recheckScheduled.current = true;

    let settle: ReturnType<typeof setTimeout>;
    const issue = setTimeout(() => {
      refetchUser({ requestPolicy: 'network-only' });
      settle = setTimeout(
        () => setRecheckDone(true),
        PERSONALIZATION_RECHECK_SETTLE_MS
      );
    }, PERSONALIZATION_RECHECK_MS);

    return () => {
      clearTimeout(issue);
      clearTimeout(settle);
    };
  }, [
    authorization.isAuthorized,
    authorization.needsPersonalization,
    refetchUser,
  ]);

  // Last resort, and only when both signals disagree: the portal says this
  // user submitted the form, and a fresh read still can't see the stamp.
  // Either the write is slower than we waited or it silently failed —
  // user-mgmt swallows that failure — and only the second case is
  // recoverable, by writing it here. Harmless if it was merely slow: the same
  // key, a slightly later timestamp.
  //
  // Gated on the marker AND a completed re-read on purpose. Writing on the
  // marker alone would make a forged `?personalized=1` permanent instead of
  // lasting one tab.
  //
  // Sends the user's existing metadata alongside the new key rather than the
  // key alone. go-api almost certainly merges — `user-preferences-api.ts`
  // writes `currency_code` on its own every time anyone changes currency, and
  // under replace that would wipe expiration_date and reset every tutorial —
  // but the array is already in hand from the query above, so there's no
  // reason to depend on that being true. Mirrors `Tutorial.tsx`, including
  // leaving expiration_date out of what it writes back.
  const [, updateUser] = useMutation(UpdateUserMutation);
  const healAttempted = useRef(false);

  useEffect(() => {
    if (!recheckDone || healAttempted.current || !userId) {
      return;
    }
    if (!authorization.isAuthorized || !authorization.needsPersonalization) {
      return;
    }
    if (!submittedThisSession.current) {
      return;
    }
    healAttempted.current = true;

    const preserved = (authorization.user?.metadata ?? []).flatMap((entry) =>
      entry?.key &&
      entry.key !== MetadataKey.PersonalizationCompleted &&
      entry.key !== MetadataKey.ExpirationDate
        ? [{ key: entry.key, value: entry.value }]
        : []
    );

    updateUser({
      id: userId,
      metadata: [
        ...preserved,
        {
          key: MetadataKey.PersonalizationCompleted,
          value: new Date().toISOString(),
        },
      ],
    }).catch((err) => {
      // Best effort — the marker already lets them through this session.
      console.warn('Could not record personalization from the dashboard', err);
    });
  }, [
    recheckDone,
    authorization.user,
    authorization.isAuthorized,
    authorization.needsPersonalization,
    userId,
    updateUser,
  ]);

  const shouldRedirectToPersonalize =
    authorization.isAuthorized &&
    authorization.needsPersonalization &&
    recheckDone &&
    !submittedThisSession.current &&
    !redirectFired;

  useEffect(() => {
    if (shouldRedirectToPersonalize) {
      setRedirectFired(true);
      window.location.replace(`${environment.ACCOUNT_PORTAL_ROOT}/personalize`);
    }
  }, [shouldRedirectToPersonalize]);

  // Hold the loader through the recheck too — painting the dashboard and then
  // navigating away from it is worse than a moment more of the spinner.
  const redirectingToPersonalize =
    shouldRedirectToPersonalize ||
    redirectFired ||
    (authorization.needsPersonalization &&
      !submittedThisSession.current &&
      !recheckDone);

  // Once the stamp is actually visible the marker has done its job — drop it,
  // so a later genuine need (metadata cleared, a different account in this
  // tab) still gets its prompt.
  useEffect(() => {
    if (authorization.isAuthorized && !authorization.needsPersonalization) {
      submittedThisSession.current = false;
      writePersonalizedThisSession(false);
    }
  }, [authorization.isAuthorized, authorization.needsPersonalization]);

  useEffect(() => {
    if (!authorization.error) {
      return;
    }

    console.error(authorization.error);
    fullLogout();
  }, [authorization.error]);

  useEffect(() => {
    if (authorization.isAuthorized && authorization.user) {
      setUser(authorization.user);
    }
  }, [authorization.isAuthorized, authorization.user, setUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const initializeRupt = async () => {
      if (isProd && userId && email && !ruptInitialized.current) {
        try {
          await Rupt.attach({
            client_id: environment.RUPT_CLIENT_ID,
            account: userId,
            email: email,
          });
          ruptInitialized.current = true;
        } catch (error) {
          console.error('Failed to initialize Rupt:', error);
        }
      }
    };
    void initializeRupt();
  }, [userId, email, isProd]);

  if (
    isAuthenticating ||
    !isAuthenticated ||
    !dashMetaCsrfToken ||
    !userId ||
    fetching ||
    !authorization.isAuthorized ||
    // Hold the loader (never paint the dashboard) while an un-personalized
    // trial user is being sent to the portal.
    redirectingToPersonalize
  ) {
    return (
      <div className={styles.loadingState}>
        <Loading />
      </div>
    );
  }

  return <AuthGateContent>{children}</AuthGateContent>;
};
